// V3 Artist IDE guardrail — Pillar 9: Gleam-Style Gated Drops and Competitions.
//
// First-class fan-growth system. Hard rules (guardrail verbatim):
//   • Verification is EXPLICIT and NEVER faked. Where a platform can't verify, the mode is
//     self_attested / manual_review / not_supported and the UI must say so.
//   • Campaigns declare reward, tasks + verification modes, dates, winner selection, delivery,
//     fraud/rate-limit controls.
//   • Competition tools: entry counter, completion tracker, approval queue, deterministic
//     winner picker, entrant export, notification template, analytics summary.
//
// Pure logic + data shapes only; persistence/routes layer on top. Deterministic where chance
// is involved (seeded winner pick) so results are auditable and testable.

// ---- verification ---------------------------------------------------------------------------------

/** How strongly a gated-campaign task's completion can honestly be verified. */
export type DJSEMTaskVerificationMode =
  | "api_verified"        // platform API confirmed the action
  | "link_click_verified" // we can only confirm the link was opened
  | "self_attested"       // fan claims completion; no platform check exists
  | "manual_review"       // artist/moderator approves each claim
  | "email_verified"      // double-opt-in email confirmation
  | "not_supported";      // platform offers no compliant path — task can't be verified

/** The kind of unlock task a fan can be asked to complete in a gated campaign. */
export type DJSEMGatedTaskKind =
  | "follow_spotify" | "follow_soundcloud" | "follow_instagram" | "follow_tiktok"
  | "subscribe_youtube" | "join_email_list" | "share_link" | "visit_link"
  | "pre_save" | "save_playlist" | "comment_on_post" | "answer_question"
  | "rsvp_event" | "submit_remix" | "upload_proof" | "refer_friend";

/**
 * The HONEST verification ceiling per task kind — the strongest mode DJSEM can offer without
 * faking platform confirmation. Follows the platform boundary table: follows/subscribes have no
 * compliant third-party verification API for ordinary apps, so their ceiling is link-click.
 */
export const TASK_VERIFICATION_CEILING: Record<DJSEMGatedTaskKind, DJSEMTaskVerificationMode> = {
  follow_spotify: "link_click_verified",
  follow_soundcloud: "link_click_verified",
  follow_instagram: "link_click_verified",
  follow_tiktok: "link_click_verified",
  subscribe_youtube: "link_click_verified",
  join_email_list: "email_verified",
  share_link: "self_attested",
  visit_link: "link_click_verified",
  pre_save: "link_click_verified",
  save_playlist: "self_attested",
  comment_on_post: "manual_review",
  answer_question: "manual_review",
  rsvp_event: "email_verified",
  submit_remix: "manual_review",
  upload_proof: "manual_review",
  refer_friend: "link_click_verified",
};

const MODE_STRENGTH: Record<DJSEMTaskVerificationMode, number> = {
  api_verified: 5, email_verified: 4, manual_review: 3, link_click_verified: 2, self_attested: 1, not_supported: 0,
};

/**
 * Clamp a requested verification mode to the honest ceiling for the task kind. Returns the
 * effective mode + a warning when the request was downgraded — the UI must surface it.
 */
export function resolveVerificationMode(kind: DJSEMGatedTaskKind, requested: DJSEMTaskVerificationMode): {
  effective: DJSEMTaskVerificationMode; downgraded: boolean; warning?: string;
} {
  const ceiling = TASK_VERIFICATION_CEILING[kind];
  if (MODE_STRENGTH[requested] <= MODE_STRENGTH[ceiling]) return { effective: requested, downgraded: false };
  return {
    effective: ceiling,
    downgraded: true,
    warning: `${kind} cannot be ${requested.replace(/_/g, " ")} — no compliant platform check exists. Using ${ceiling.replace(/_/g, " ")} instead.`,
  };
}

// ---- campaign shapes ------------------------------------------------------------------------------

/** The kind of reward unlocked by completing a gated campaign's tasks. */
export type DJSEMGatedRewardKind =
  | "exclusive_download" | "private_track" | "sample_pack" | "remix_stems"
  | "show_pack_preview" | "visual_pack_preview" | "private_playlist" | "discount_code"
  | "early_access" | "behind_the_scenes" | "competition_entry" | "guestlist_entry"
  | "merch_giveaway" | "pre_save_reward" | "fan_club_reward";

/** A single unlock task within a gated campaign. */
export interface DJSEMGatedTask {
  id: string;
  kind: DJSEMGatedTaskKind;
  label: string;
  url?: string;
  required: boolean;
  verification: DJSEMTaskVerificationMode;
}

/** How winners are chosen from qualified entries. */
export type DJSEMWinnerSelectionMethod = "random_draw" | "first_n" | "manual_pick" | "all_qualify";

/** Anti-fraud / rate-limiting rules for a gated campaign's entries. */
export interface DJSEMFraudControls {
  maxEntriesPerFan: number;          // rate limit per fan profile
  maxEntriesPerIpPerDay: number;     // coarse abuse control
  requireEmailVerification: boolean; // gate entry on double opt-in
  duplicateEmailPolicy: "block" | "merge" | "allow";
}

/** A full gated-drop/competition campaign: reward, tasks, winner selection, and fraud controls. */
export interface DJSEMGatedCampaign {
  id: string;
  organisationId?: string;
  projectId?: string;
  title: string;
  rewardKind: DJSEMGatedRewardKind;
  rewardDescription: string;
  tasks: DJSEMGatedTask[];
  winnerSelection: DJSEMWinnerSelectionMethod;
  winnerCount: number;
  startsAt?: string;
  endsAt?: string;
  termsUrl?: string;
  rulesText?: string;
  publicPageSlug?: string;
  emailCaptureEnabled: boolean;
  fraud: DJSEMFraudControls;
  status: "draft" | "published" | "closed" | "winners_selected" | "delivered";
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** Conservative default fraud controls applied to new campaigns unless overridden. */
export const DEFAULT_FRAUD_CONTROLS: DJSEMFraudControls = {
  maxEntriesPerFan: 1,
  maxEntriesPerIpPerDay: 25,
  requireEmailVerification: true,
  duplicateEmailPolicy: "block",
};

// ---- campaign builder + validation ---------------------------------------------------------------

/** Input for {@link createGatedCampaign}. */
export interface CreateGatedCampaignInput {
  title: string;
  rewardKind: DJSEMGatedRewardKind;
  rewardDescription?: string;
  tasks: Array<{ kind: DJSEMGatedTaskKind; label?: string; url?: string; required?: boolean; requestedVerification?: DJSEMTaskVerificationMode }>;
  winnerSelection?: DJSEMWinnerSelectionMethod;
  winnerCount?: number;
  startsAt?: string;
  endsAt?: string;
  termsUrl?: string;
  rulesText?: string;
  emailCaptureEnabled?: boolean;
  fraud?: Partial<DJSEMFraudControls>;
  organisationId?: string;
  projectId?: string;
  createdBy?: string;
}

/** The result of building a gated campaign draft: the campaign plus verification-downgrade warnings and any publish blockers. */
export interface CreateGatedCampaignResult {
  campaign: DJSEMGatedCampaign;
  warnings: string[];   // includes every verification downgrade — the UI must show them
  blockers: string[];   // empty = publishable draft
}

let seq = 0;
function newId(prefix: string, now: string): string {
  seq += 1;
  return `${prefix}_${now.replace(/[-:.TZ]/g, "").slice(0, 14)}_${seq}`;
}

const DEFAULT_TASK_LABEL: Record<DJSEMGatedTaskKind, string> = {
  follow_spotify: "Follow on Spotify", follow_soundcloud: "Follow on SoundCloud",
  follow_instagram: "Follow on Instagram", follow_tiktok: "Follow on TikTok",
  subscribe_youtube: "Subscribe on YouTube", join_email_list: "Join the email list",
  share_link: "Share the link", visit_link: "Visit the link", pre_save: "Pre-save the release",
  save_playlist: "Save the playlist", comment_on_post: "Comment on the post",
  answer_question: "Answer a question", rsvp_event: "RSVP to the event",
  submit_remix: "Submit a remix", upload_proof: "Upload proof", refer_friend: "Refer a friend",
};

/**
 * Build a validated draft campaign. Never fakes verification: every task's mode is clamped to
 * its honest ceiling and each downgrade produces a user-visible warning.
 */
export function createGatedCampaign(input: CreateGatedCampaignInput, now = new Date().toISOString()): CreateGatedCampaignResult {
  const warnings: string[] = [];
  const blockers: string[] = [];

  if (!input.title?.trim()) blockers.push("campaign title is required");
  if (!input.tasks?.length) blockers.push("at least one unlock task is required");
  if (input.winnerSelection === "random_draw" && !(input.winnerCount && input.winnerCount > 0)) {
    blockers.push("random draw needs winnerCount ≥ 1");
  }
  if (input.startsAt && input.endsAt && input.startsAt >= input.endsAt) {
    blockers.push("endsAt must be after startsAt");
  }
  const competitionLike = input.winnerSelection === "random_draw" || input.rewardKind === "competition_entry" || input.rewardKind === "merch_giveaway";
  if (competitionLike && !input.termsUrl && !input.rulesText) {
    blockers.push("competitions/giveaways need rulesText or termsUrl (regional prize-draw law)");
  }

  const tasks: DJSEMGatedTask[] = (input.tasks ?? []).map((t, i) => {
    const requested = t.requestedVerification ?? TASK_VERIFICATION_CEILING[t.kind];
    const { effective, downgraded, warning } = resolveVerificationMode(t.kind, requested);
    if (downgraded && warning) warnings.push(warning);
    if (effective === "not_supported") warnings.push(`${t.kind}: no compliant verification exists — the task will be shown as unverifiable.`);
    return {
      id: `task_${i + 1}`,
      kind: t.kind,
      label: t.label?.trim() || DEFAULT_TASK_LABEL[t.kind],
      url: t.url,
      required: t.required ?? true,
      verification: effective,
    };
  });

  const campaign: DJSEMGatedCampaign = {
    id: newId("camp", now),
    organisationId: input.organisationId,
    projectId: input.projectId,
    title: input.title?.trim() ?? "",
    rewardKind: input.rewardKind,
    rewardDescription: input.rewardDescription?.trim() || "",
    tasks,
    winnerSelection: input.winnerSelection ?? "all_qualify",
    winnerCount: input.winnerCount ?? 1,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    termsUrl: input.termsUrl,
    rulesText: input.rulesText,
    emailCaptureEnabled: input.emailCaptureEnabled ?? true,
    fraud: { ...DEFAULT_FRAUD_CONTROLS, ...input.fraud },
    status: "draft",
    createdBy: input.createdBy,
    createdAt: now,
    updatedAt: now,
  };
  return { campaign, warnings, blockers };
}

// ---- entries + competition tools ------------------------------------------------------------------

/** A single fan's entry into a gated campaign, with per-task completion state and approval status. */
export interface DJSEMCompetitionEntry {
  id: string;
  campaignId: string;
  fanEmail?: string;
  fanName?: string;
  taskStates: Array<{ taskId: string; completed: boolean; verification: DJSEMTaskVerificationMode; proofUrl?: string }>;
  approved: boolean | null;   // null = pending review (manual_review tasks)
  createdAt: string;
}

/** Entry counter + completion tracker + approval queue in one pure summary. */
export function summariseEntries(campaign: DJSEMGatedCampaign, entries: DJSEMCompetitionEntry[]): {
  total: number;
  qualified: number;
  pendingReview: number;
  rejected: number;
  taskCompletion: Array<{ taskId: string; label: string; completedCount: number }>;
} {
  const requiredIds = campaign.tasks.filter((t) => t.required).map((t) => t.id);
  let qualified = 0, pendingReview = 0, rejected = 0;
  const completion = new Map<string, number>();
  for (const e of entries) {
    const doneIds = new Set(e.taskStates.filter((s) => s.completed).map((s) => s.taskId));
    for (const id of doneIds) completion.set(id, (completion.get(id) ?? 0) + 1);
    const allRequiredDone = requiredIds.every((id) => doneIds.has(id));
    if (e.approved === false) rejected += 1;
    else if (e.approved === null && e.taskStates.some((s) => s.verification === "manual_review" && s.completed)) pendingReview += 1;
    else if (allRequiredDone) qualified += 1;
  }
  return {
    total: entries.length,
    qualified, pendingReview, rejected,
    taskCompletion: campaign.tasks.map((t) => ({ taskId: t.id, label: t.label, completedCount: completion.get(t.id) ?? 0 })),
  };
}

/**
 * Deterministic winner pick — mulberry32 seeded PRNG over the qualified entries so a draw is
 * reproducible and auditable (same seed → same winners). Never picks rejected/pending entries.
 */
export function pickWinners(campaign: DJSEMGatedCampaign, entries: DJSEMCompetitionEntry[], seed: number): DJSEMCompetitionEntry[] {
  const requiredIds = campaign.tasks.filter((t) => t.required).map((t) => t.id);
  const qualified = entries.filter((e) => {
    if (e.approved === false) return false;
    if (e.approved === null && e.taskStates.some((s) => s.verification === "manual_review" && s.completed)) return false;
    const done = new Set(e.taskStates.filter((s) => s.completed).map((s) => s.taskId));
    return requiredIds.every((id) => done.has(id));
  });
  if (campaign.winnerSelection === "all_qualify") return qualified;
  if (campaign.winnerSelection === "first_n") return qualified.slice(0, campaign.winnerCount);
  if (campaign.winnerSelection === "manual_pick") return []; // manual: the UI picks; nothing automatic

  // random_draw — seeded Fisher-Yates
  let s = seed >>> 0;
  const rand = () => {
    s |= 0; s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const pool = [...qualified];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, campaign.winnerCount);
}

/** CSV export of entrants (email capture consented via the campaign's own opt-in). */
export function exportEntrantsCsv(entries: DJSEMCompetitionEntry[]): string {
  const header = "entry_id,name,email,approved,created_at";
  const rows = entries.map((e) =>
    [e.id, e.fanName ?? "", e.fanEmail ?? "", e.approved === null ? "pending" : String(e.approved), e.createdAt]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
  );
  return [header, ...rows].join("\n");
}

/** Winner notification template — plain copy the artist can edit before sending. */
export function winnerNotificationTemplate(campaign: DJSEMGatedCampaign, winnerName?: string): string {
  return [
    `Subject: You won — ${campaign.title}`,
    "",
    `Hi ${winnerName || "there"},`,
    "",
    `Great news — you're a winner in "${campaign.title}".`,
    `Your reward: ${campaign.rewardDescription || campaign.rewardKind.replace(/_/g, " ")}.`,
    "",
    "Reply to this email and we'll sort out delivery.",
    "",
    "Thanks for being part of it.",
  ].join("\n");
}

/** Campaign analytics summary — pure aggregation, no tracking pixels implied. */
export function campaignAnalytics(campaign: DJSEMGatedCampaign, entries: DJSEMCompetitionEntry[]): {
  totalEntries: number;
  qualifiedRate: number;      // 0..1
  taskDropoff: Array<{ taskId: string; label: string; completionRate: number }>;
  emailCaptured: number;
} {
  const summary = summariseEntries(campaign, entries);
  const total = Math.max(1, summary.total);
  return {
    totalEntries: summary.total,
    qualifiedRate: summary.qualified / total,
    taskDropoff: summary.taskCompletion.map((t) => ({ taskId: t.taskId, label: t.label, completionRate: t.completedCount / total })),
    emailCaptured: entries.filter((e) => !!e.fanEmail).length,
  };
}
